import type { SupabaseClient } from '@supabase/supabase-js';
import {
  calculatePairingCompatibility,
  disagreementSeverity,
  normalizePairingIdentity,
  pairingCompatibilityAlgorithmVersion,
  type CalculatedCompatibility,
  type PairingCalculationConfig,
  type PairingLineEvidence,
  type ScoreCompleteness,
} from './pairingCompatibilityAnalytics';
import { normalizeCompatibility, type PartnerCompatibility } from './partnerPreferences';
import { loadPairingCompatibilityConfig } from './pairingCompatibilitySettings';

type TeamRow = { id: string; workspace_id: string; team_name: string | null };
type MatchRow = {
  id: string;
  team_id: string;
  match_date: string;
  opponent_name: string | null;
  lifecycle_state: string | null;
  match_status?: string | null;
  deleted_at?: string | null;
};
type MatchResultRow = { match_event_id: string; result_status: string | null };
type PositionRow = { id: string; position_code: string | null; display_order: number | null; line_type: string | null };
type EntryRow = { match_event_id: string; league_configuration_position_id: string; player_id: string; slot_number: number | null; deleted_at?: string | null };
type LineResultRow = {
  id: string;
  match_event_id: string;
  league_configuration_position_id: string;
  position_code: string | null;
  line_number: number | null;
  line_type: string | null;
  winner: 'Us' | 'Opponent' | 'Unknown' | null;
  result_value: string | null;
  retired: boolean | null;
  defaulted: boolean | null;
  walkover: boolean | null;
  set1_us_games: number | null;
  set1_opponent_games: number | null;
  set2_us_games: number | null;
  set2_opponent_games: number | null;
  set3_us_games: number | null;
  set3_opponent_games: number | null;
  score_completeness?: ScoreCompleteness | null;
  set3_is_match_tiebreak?: boolean | null;
  match_tiebreak_exact_score_known?: boolean | null;
  match_tiebreak_us_points?: number | null;
  match_tiebreak_opponent_points?: number | null;
  match_tiebreak_winner?: 'Us' | 'Opponent' | 'Unknown' | null;
  deleted_at?: string | null;
};
type PreferenceRow = {
  id: string;
  workspace_id: string;
  team_id: string | null;
  player1_id: string;
  player2_id: string;
  compatibility: PartnerCompatibility | null;
  calculated_compatibility?: CalculatedCompatibility | null;
  compatibility_source?: string | null;
  review_status?: string | null;
};

export type PairingCompatibilityBackfillSummary = {
  workspaceId: string;
  completedDoublesLines: number;
  pairingsEvaluated: number;
  ratingsEstablished: number;
  ratingsUpdated: number;
  reviewFlags: number;
  skippedInsufficientEvidence: number;
  incompleteScoreLines: number;
  diagnostics: {
    workspaceTeams: number;
    candidateMatches: number;
    matchResultsLoaded: number;
    eligibleMatches: number;
    doublesPositionsLoaded: number;
    lineResultsLoaded: number;
    lineupEntriesLoaded: number;
    playableDoublesLines: number;
    defaultedLinesSkipped: number;
    linesWithoutTwoPlayers: number;
  };
  errors: string[];
};

export type PairingCompatibilityBackfillOptions = {
  workspaceId: string;
  suppressNotifications?: boolean;
  auditUserId?: string | null;
  config?: PairingCalculationConfig | null;
};

export async function runPairingCompatibilityBackfill(
  supabase: SupabaseClient,
  options: PairingCompatibilityBackfillOptions,
): Promise<PairingCompatibilityBackfillSummary> {
  const summary: PairingCompatibilityBackfillSummary = {
    workspaceId: options.workspaceId,
    completedDoublesLines: 0,
    pairingsEvaluated: 0,
    ratingsEstablished: 0,
    ratingsUpdated: 0,
    reviewFlags: 0,
    skippedInsufficientEvidence: 0,
    incompleteScoreLines: 0,
    diagnostics: {
      workspaceTeams: 0,
      candidateMatches: 0,
      matchResultsLoaded: 0,
      eligibleMatches: 0,
      doublesPositionsLoaded: 0,
      lineResultsLoaded: 0,
      lineupEntriesLoaded: 0,
      playableDoublesLines: 0,
      defaultedLinesSkipped: 0,
      linesWithoutTwoPlayers: 0,
    },
    errors: [],
  };
  const config = options.config || await loadPairingCompatibilityConfig();

  const { data: teams, error: teamsError } = await supabase
    .from('teams')
    .select('id, workspace_id, team_name')
    .eq('workspace_id', options.workspaceId)
    .is('deleted_at', null);
  if (teamsError) return withError(summary, teamsError.message);
  const teamRows = (teams || []) as TeamRow[];
  const teamIds = teamRows.map((team) => team.id);
  summary.diagnostics.workspaceTeams = teamIds.length;
  if (teamIds.length === 0) return summary;
  const teamById = new Map(teamRows.map((team) => [team.id, team]));

  const { data: matches, error: matchesError } = await supabase
    .from('match_events')
    .select('id, team_id, match_date, opponent_name, lifecycle_state, match_status')
    .in('team_id', teamIds)
    .is('deleted_at', null)
    .order('match_date', { ascending: true });
  if (matchesError) return withError(summary, matchesError.message);
  const matchRows = (matches || []) as MatchRow[];
  const candidateMatchIds = matchRows.map((match) => match.id);
  summary.diagnostics.candidateMatches = candidateMatchIds.length;
  if (candidateMatchIds.length === 0) return summary;

  const { data: matchResults, error: matchResultsError } = await supabase
    .from('match_results')
    .select('match_event_id, result_status')
    .in('match_event_id', candidateMatchIds)
    .is('deleted_at', null);
  if (matchResultsError) return withError(summary, matchResultsError.message);
  summary.diagnostics.matchResultsLoaded = (matchResults || []).length;
  const completedResultMatchIds = new Set(
    ((matchResults || []) as MatchResultRow[])
      .filter((result) => ['Confirmed', 'Submitted'].includes(String(result.result_status || '')))
      .map((result) => result.match_event_id),
  );
  const matchIds = matchRows
    .filter((match) => match.lifecycle_state === 'completed' || match.match_status === 'Completed' || completedResultMatchIds.has(match.id))
    .map((match) => match.id);
  summary.diagnostics.eligibleMatches = matchIds.length;
  if (matchIds.length === 0) return summary;
  const matchById = new Map(matchRows.map((match) => [match.id, match]));

  const [positionsResult, entriesResult, lineResultsResult, preferenceResult] = await Promise.all([
    supabase.from('league_configuration_positions').select('id, position_code, display_order, line_type').in('line_type', ['Doubles', 'doubles', 'Mixed Doubles', 'mixed_doubles']),
    supabase.from('lineup_entries').select('match_event_id, league_configuration_position_id, player_id, slot_number').in('match_event_id', matchIds).is('deleted_at', null),
    supabase
      .from('line_results')
      .select('id, match_event_id, league_configuration_position_id, position_code, line_number, line_type, winner, result_value, retired, defaulted, walkover, set1_us_games, set1_opponent_games, set2_us_games, set2_opponent_games, set3_us_games, set3_opponent_games, score_completeness, set3_is_match_tiebreak, match_tiebreak_exact_score_known, match_tiebreak_us_points, match_tiebreak_opponent_points, match_tiebreak_winner')
      .in('match_event_id', matchIds)
      .is('deleted_at', null),
    supabase.from('partner_preferences').select('id, workspace_id, team_id, player1_id, player2_id, compatibility, calculated_compatibility, compatibility_source, review_status').eq('workspace_id', options.workspaceId).is('deleted_at', null),
  ]);
  const firstError = positionsResult.error || entriesResult.error || lineResultsResult.error || preferenceResult.error;
  if (firstError) return withError(summary, firstError.message);

  const doublesPositionIds = new Set(((positionsResult.data || []) as PositionRow[]).filter((position) => isDoubles(position.line_type)).map((position) => position.id));
  summary.diagnostics.doublesPositionsLoaded = doublesPositionIds.size;
  summary.diagnostics.lineResultsLoaded = (lineResultsResult.data || []).length;
  summary.diagnostics.lineupEntriesLoaded = (entriesResult.data || []).length;
  const entriesByLine = new Map<string, EntryRow[]>();
  ((entriesResult.data || []) as EntryRow[]).forEach((entry) => {
    const key = lineKey(entry.match_event_id, entry.league_configuration_position_id);
    entriesByLine.set(key, [...(entriesByLine.get(key) || []), entry].sort((a, b) => Number(a.slot_number || 0) - Number(b.slot_number || 0)));
  });
  const preferencesByPair = new Map<string, PreferenceRow>();
  ((preferenceResult.data || []) as PreferenceRow[]).forEach((preference) => preferencesByPair.set(pairKey(preference.workspace_id, preference.player1_id, preference.player2_id), preference));

  const evidenceByPair = new Map<string, PairingLineEvidence[]>();
  ((lineResultsResult.data || []) as LineResultRow[]).forEach((line) => {
    if (!doublesPositionIds.has(line.league_configuration_position_id) && !isDoubles(line.line_type)) return;
    const match = matchById.get(line.match_event_id);
    if (!match) return;
    const team = teamById.get(match.team_id);
    if (!team || team.workspace_id !== options.workspaceId) return;
    if (isDefaultLine(line)) {
      summary.diagnostics.defaultedLinesSkipped += 1;
      return;
    }
    summary.diagnostics.playableDoublesLines += 1;
    const entries = entriesByLine.get(lineKey(line.match_event_id, line.league_configuration_position_id)) || [];
    const playerIds = [...new Set(entries.map((entry) => entry.player_id).filter(Boolean))];
    if (playerIds.length !== 2) {
      summary.diagnostics.linesWithoutTwoPlayers += 1;
      return;
    }
    const [player1, player2] = normalizePairingIdentity(playerIds[0], playerIds[1]);
    const key = pairKey(options.workspaceId, player1, player2);
    const evidence = lineToEvidence(line, match);
    evidenceByPair.set(key, [...(evidenceByPair.get(key) || []), evidence]);
    summary.completedDoublesLines += 1;
    if (line.score_completeness && !['Complete', 'Result Complete', 'Special Result'].includes(line.score_completeness)) summary.incompleteScoreLines += 1;
  });

  for (const [key, evidence] of evidenceByPair) {
    summary.pairingsEvaluated += 1;
    const [, player1Id, player2Id] = key.split(':');
    const existing = preferencesByPair.get(key) || null;
    // Each Team is one competitive season. Prefer the pairing's explicit Team;
    // workspace-wide pairings use the Team from their latest played line.
    const latestTeamId = [...evidence].sort((a, b) => b.matchDate.localeCompare(a.matchDate))[0]?.seasonId || null;
    const currentTeamId = existing?.team_id && evidence.some((line) => line.seasonId === existing.team_id)
      ? existing.team_id
      : latestTeamId;
    const calculation = calculatePairingCompatibility(evidence, { currentSeasonId: currentTeamId, config });
    if (!calculation.sufficientEvidence || !calculation.compatibility || calculation.numericScore == null || !calculation.confidence) {
      summary.skippedInsufficientEvidence += 1;
      continue;
    }

    const visible = normalizeCompatibility(existing?.compatibility || 'Unknown');
    const canAdoptCalculated = !existing || visible === 'Unknown' || existing.compatibility_source === 'Data Driven';
    const nextVisible = canAdoptCalculated ? calculation.compatibility : visible;
    const severity = canAdoptCalculated ? 'None' : disagreementSeverity(visible, calculation.compatibility);
    const needsReview = severity !== 'None';
    const payload = {
      workspace_id: options.workspaceId,
      player1_id: player1Id,
      player2_id: player2Id,
      compatibility: nextVisible,
      calculated_compatibility: calculation.compatibility,
      calculated_confidence: calculation.confidence,
      compatibility_source: canAdoptCalculated ? 'Data Driven' : existing?.compatibility_source || 'Captain Set',
      review_status: needsReview ? 'needs_review' : 'none',
      review_severity: severity,
      calculated_score: calculation.numericScore,
      calculated_at: new Date().toISOString(),
      algorithm_version: pairingCompatibilityAlgorithmVersion,
      calculated_match_count: calculation.metrics.matchesPlayed,
      calculated_current_season_record: `${calculation.metrics.currentSeasonRecord.wins}-${calculation.metrics.currentSeasonRecord.losses}`,
      calculated_average_game_differential: calculation.metrics.averageRegulationGameDifferential,
      calculated_match_tiebreak_record: `${calculation.metrics.decidingMatchTiebreakWins}-${calculation.metrics.decidingMatchTiebreakLosses}`,
      calculated_comeback_wins: calculation.metrics.firstSetComebackWins,
      calculated_trend_direction: calculation.trendDirection,
      calculated_match_story: calculation.matchStory,
      calculated_metrics: calculation.metrics,
      is_active: true,
      updated_by: options.auditUserId || null,
    };

    const writeResult = existing
      ? await supabase.from('partner_preferences').update(payload).eq('id', existing.id)
      : await supabase.from('partner_preferences').insert({ ...payload, pairing_rule: 'none', notes: null, created_by: options.auditUserId || null });
    if (writeResult.error) {
      summary.errors.push(writeResult.error.message);
      continue;
    }

    const preferenceId = existing?.id || await fetchPreferenceId(supabase, options.workspaceId, player1Id, player2Id);
    if (!existing) summary.ratingsEstablished += 1;
    else if (canAdoptCalculated || existing.calculated_compatibility !== calculation.compatibility) summary.ratingsUpdated += 1;
    if (needsReview) summary.reviewFlags += 1;

    if (preferenceId) {
      await supabase.from('partner_compatibility_calculation_snapshots').insert({
        partner_preference_id: preferenceId,
        workspace_id: options.workspaceId,
        player1_id: player1Id,
        player2_id: player2Id,
        calculated_compatibility: calculation.compatibility,
        previous_calculated_compatibility: existing?.calculated_compatibility || null,
        numeric_score: calculation.numericScore,
        confidence: calculation.confidence,
        algorithm_version: pairingCompatibilityAlgorithmVersion,
        matches_included: calculation.matchesIncluded,
        match_history_cutoff_date: evidence[evidence.length - 1]?.matchDate || null,
        component_scores: calculation.componentScores,
        match_count: calculation.metrics.matchesPlayed,
        match_record: { wins: calculation.metrics.matchesWon, losses: calculation.metrics.matchesLost },
        sets_won: calculation.metrics.setsWon,
        sets_lost: calculation.metrics.setsLost,
        regulation_games_won: calculation.metrics.regulationGamesWon,
        regulation_games_lost: calculation.metrics.regulationGamesLost,
        average_game_differential: calculation.metrics.averageRegulationGameDifferential,
        straight_set_record: { wins: calculation.metrics.straightSetWins, losses: calculation.metrics.straightSetLosses },
        match_tiebreak_record: { wins: calculation.metrics.decidingMatchTiebreakWins, losses: calculation.metrics.decidingMatchTiebreakLosses },
        comeback_wins: calculation.metrics.firstSetComebackWins,
        losses_after_winning_first_set: calculation.metrics.lossesAfterWinningFirstSet,
        score_completeness_summary: calculation.metrics.scoreCompletenessSummary,
        generated_match_story: calculation.matchStory,
        metrics: calculation.metrics,
      });
    }

    if (needsReview && !options.suppressNotifications) {
      await insertReviewNotification(supabase, options.workspaceId, preferenceId, player1Id, player2Id, calculation.compatibility);
    }
  }

  return summary;
}

function withError(summary: PairingCompatibilityBackfillSummary, message: string): PairingCompatibilityBackfillSummary {
  summary.errors.push(message);
  return summary;
}

function isDoubles(lineType?: string | null): boolean {
  return String(lineType || '').toLowerCase().includes('double');
}

function isDefaultLine(line: LineResultRow): boolean {
  const resultValue = String(line.result_value || '').toLowerCase();
  return Boolean(
    line.defaulted
    || line.walkover
    || resultValue === 'default by opponent'
    || resultValue === 'default by us'
    || resultValue === 'double default',
  );
}

function lineKey(matchId: string, positionId: string): string {
  return `${matchId}:${positionId}`;
}

function pairKey(workspaceId: string, player1Id: string, player2Id: string): string {
  const [one, two] = normalizePairingIdentity(player1Id, player2Id);
  return `${workspaceId}:${one}:${two}`;
}

function lineToEvidence(line: LineResultRow, match: MatchRow): PairingLineEvidence {
  return {
    matchId: line.match_event_id,
    matchDate: match.match_date,
    seasonId: match.team_id,
    positionCode: line.position_code,
    lineNumber: line.line_number,
    lineType: line.line_type,
    opponentName: match.opponent_name,
    winner: line.winner || 'Unknown',
    resultValue: line.result_value,
    retired: line.retired,
    defaulted: line.defaulted,
    walkover: line.walkover,
    sets: [
      { us: line.set1_us_games, opponent: line.set1_opponent_games },
      { us: line.set2_us_games, opponent: line.set2_opponent_games },
      { us: line.set3_us_games, opponent: line.set3_opponent_games },
    ],
    matchTiebreakWinner: line.match_tiebreak_winner,
    matchTiebreakExactScoreKnown: line.match_tiebreak_exact_score_known,
    matchTiebreakUsPoints: line.match_tiebreak_us_points,
    matchTiebreakOpponentPoints: line.match_tiebreak_opponent_points,
  };
}

async function fetchPreferenceId(supabase: SupabaseClient, workspaceId: string, player1Id: string, player2Id: string): Promise<string | null> {
  const { data } = await supabase
    .from('partner_preferences')
    .select('id')
    .eq('workspace_id', workspaceId)
    .eq('player1_id', player1Id)
    .eq('player2_id', player2Id)
    .is('deleted_at', null)
    .maybeSingle();
  return data && typeof data === 'object' && 'id' in data ? String(data.id) : null;
}

async function insertReviewNotification(
  supabase: SupabaseClient,
  workspaceId: string,
  preferenceId: string | null,
  player1Id: string,
  player2Id: string,
  calculated: string,
): Promise<void> {
  const { data: team } = await supabase
    .from('teams')
    .select('id')
    .eq('workspace_id', workspaceId)
    .is('deleted_at', null)
    .limit(1)
    .maybeSingle();
  const teamId = team && typeof team === 'object' && 'id' in team ? String(team.id) : null;
  await supabase.from('notification_instances').upsert({
    notification_key: `pairing-compatibility-review:${preferenceId || `${workspaceId}:${player1Id}:${player2Id}`}`,
    notification_type: 'pairing_compatibility_needs_review',
    category: 'Action Required',
    severity: 'Warning',
    title: 'Pairing compatibility needs review',
    message: `A calculated ${calculated} compatibility rating disagrees with the visible captain rating.`,
    team_id: teamId,
    action_label: 'Review Pairing',
    action_route: teamId ? `team-roster:${teamId}` : null,
    dismissed: false,
    resolved: false,
    updated_at: new Date().toISOString(),
  }, { onConflict: 'notification_key' });
}
